import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon } from 'lucide-react';
import { useId, useState, type JSX } from 'react';

import { Card, CardContent, CardHeader } from '@/components/ui/card.js';
import { runLogsQueryOptions } from '@/lib/api/quality.js';
import { cn } from '@/lib/utils.js';

import {
  GenerationLogLines,
  MAX_VISIBLE_LOG_LINES,
  type RunLogVariant,
} from './generation-log-lines.js';

/** The saved log's title per run kind. */
const LOG_TITLES: Record<RunLogVariant, string> = {
  generation: 'Generation log',
  exploratory: 'Exploration log',
};

/**
 * The saved run output — the same lines the live feed showed while a generation or exploratory run
 * was working, kept from the run's log tail (archived to S3 once the run is done). Its own
 * collapsible, collapsed by default. Renders nothing when the run has no log.
 */
export function GenerationLog({
  runId,
  variant = 'generation',
}: {
  runId: string;
  variant?: RunLogVariant;
}): JSX.Element | null {
  const title = LOG_TITLES[variant];
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const { data } = useQuery(runLogsQueryOptions(runId));
  const lines = (data ?? []).slice(-MAX_VISIBLE_LOG_LINES);
  if (lines.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          className="flex w-full items-center gap-2 text-left text-sm font-semibold"
        >
          <ChevronDownIcon
            className={cn('size-4 transition-transform', open && 'rotate-180')}
            aria-hidden
          />
          {title}
        </button>
      </CardHeader>
      {open ? (
        <CardContent id={bodyId}>
          <GenerationLogLines lines={lines} ariaLabel={title} />
        </CardContent>
      ) : null}
    </Card>
  );
}
